import { useCallback, useEffect, useMemo, useState } from 'react';

import { findEvolvingBudgetId } from '../budgetUtils';

export const GRANULARITY_MONTH = 'M';
export const GRANULARITY_QUARTER = 'Q';
export const GRANULARITY_YEAR = 'Y';

export const PRESET_YTD = 'ytd';
export const PRESET_LAST_12_MONTHS = 'last_12_months';
export const PRESET_CURRENT_FY = 'current_fy';
export const PRESET_LIFETIME = 'lifetime';
export const PRESET_CUSTOM = 'custom';

const DEFAULT_GRANULARITY = GRANULARITY_YEAR;

export const TAB_BY_PROJECT = 'by_project';
export const TAB_BY_PERIOD = 'by_period';
export const TAB_CASHFLOW = 'cashflow';

const LIFETIME = { from: null, to: null, presetId: PRESET_LIFETIME };

const storageKey = (organisationId) => `org-summary-filters:${organisationId}`;

const readPersisted = (organisationId) => {
    try {
        const raw = window.localStorage.getItem(storageKey(organisationId));

        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};

const writePersisted = (organisationId, value) => {
    try {
        window.localStorage.setItem(storageKey(organisationId), JSON.stringify(value));
    } catch (e) {
        // localStorage may be unavailable (private mode / quota); persistence is best-effort.
    }
};

/**
 * Owns the org-summary filter state: project selection (filterData), date range
 * and granularity. The selection and granularity persist per organisation and
 * hydrate back, intersecting persisted project ids with the live events so
 * stale selections are dropped. The page always opens on Lifetime, whose dates
 * the server resolves from the selected projects (both bounds null here).
 *
 * filterData stays the canonical { [eventId]: { enabled, budget } } shape that the
 * export + summary.data contract depends on.
 */
export default function useSummaryFilters(organisationId, events) {
    const [filterData, setFilterData] = useState({});
    const [dateRange, setDateRange] = useState(LIFETIME);
    const [granularity, setGranularityState] = useState(DEFAULT_GRANULARITY);
    const [activeTab, setActiveTab] = useState(TAB_BY_PROJECT);
    const [hydrated, setHydrated] = useState(false);

    // Hydrate once the events payload has arrived: build the default selection
    // (every event enabled on its evolving/non-locked budget), then layer any
    // persisted choices that still map to a live event on top.
    useEffect(() => {
        if (hydrated || !events.length) {
            return;
        }

        const persisted = readPersisted(organisationId);
        const persistedFilters = persisted?.filterData || {};

        const nextFilterData = {};
        events.forEach((event) => {
            const persistedEntry = persistedFilters[event.id];
            nextFilterData[event.id] = {
                enabled: persistedEntry ? !!persistedEntry.enabled : true,
                budget: findEvolvingBudgetId(event),
            };
        });

        setFilterData(nextFilterData);

        if (persisted?.granularity) {
            setGranularityState(persisted.granularity);
        }

        setHydrated(true);
    }, [events, hydrated, organisationId]);

    // Persist whenever the user's choices change (after hydration).
    useEffect(() => {
        if (!hydrated) {
            return;
        }

        writePersisted(organisationId, { filterData, granularity });
    }, [filterData, granularity, hydrated, organisationId]);

    const selectedEvents = useMemo(() => events.filter((event) => filterData[event.id]?.enabled), [events, filterData]);

    const selectedCount = selectedEvents.length;

    const currencyStatus = useMemo(() => {
        const codes = new Set();
        selectedEvents.forEach((event) => {
            if (event.currency?.code) {
                codes.add(event.currency.code);
            }
        });

        return { mixed: codes.size > 1 };
    }, [selectedEvents]);

    const removeProject = useCallback((eventId) => {
        setFilterData((prev) => ({
            ...prev,
            [eventId]: { ...prev[eventId], enabled: false },
        }));
    }, []);

    // Bulk-replace the whole selection map. Used by the picker's draft-then-commit
    // Apply: the popover owns a local draft and commits it in one shot, keeping the
    // canonical { [eventId]: { enabled, budget } } shape intact.
    const setSelection = useCallback((nextFilterData) => {
        setFilterData(nextFilterData || {});
    }, []);

    const setRange = useCallback((from, to, presetId = PRESET_CUSTOM) => {
        setDateRange({ from: from || null, to: to || null, presetId });
    }, []);

    const setLifetime = useCallback(() => setDateRange(LIFETIME), []);

    const setGranularity = useCallback((value) => {
        setGranularityState(value);
    }, []);

    return {
        hydrated,
        filterData,
        dateRange,
        granularity,
        activeTab,
        selectedEvents,
        selectedCount,
        currencyStatus,
        removeProject,
        setSelection,
        setRange,
        setLifetime,
        setGranularity,
        setActiveTab,
    };
}
